import React from 'react';
import PropTypes from 'prop-types';
import { range } from 'lodash';
import FontIcon from 'components/FontIcon';
import { Default, Mobile } from 'components/Responsive';
import classes from './PaginationA.module.scss';

const PaginationA = (props) => {
  const {
    pageCount = 0,
    setActivePage,
    activePage = 1,
    containerRef = {},
  } = props;

  const resetScrollEffect = ({ element = {} }) => {
    if (typeof window !== 'undefined') {
      const { current = {} } = element;
      window.scrollTo({
        top: current.offsetTop,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleNavigation = (page) => {
    setActivePage(page);
    resetScrollEffect({ element: containerRef });
  };

  const getPageset = (totalPages, maxPagesOnDisplay, currentPage) => {
    // Default spaces for shifting
    let leftShift = Math.floor(maxPagesOnDisplay / 2);
    let rightShift = maxPagesOnDisplay - leftShift - 1;
    const middleElement = leftShift + 1;

    // If there is no enough pages we can render the initial set: [1...totalPages]
    if (totalPages <= maxPagesOnDisplay) return range(1, totalPages + 1);
    // If you still are on the middle element we can render the initial set: [1...10]
    if (currentPage <= middleElement) return range(1, maxPagesOnDisplay + 1);
    // If we need to shift pages, we need to add an special consideration for when you are reaching the end of the set
    if (totalPages - currentPage < rightShift) {
      // You are getting closer so reduce the number of items to the right of the current page
      rightShift = totalPages - currentPage;
      // and increase the number of items to the left of the current page
      leftShift = maxPagesOnDisplay - (rightShift + 1);
    }

    // Now that we calculate how many items to the right & left, we construct the new range of pages
    return [...range(currentPage - leftShift, currentPage), ...range(currentPage, currentPage + rightShift + 1)];
  };

  const renderPageNumberButton = (pNumber) => (
    <button
      type='button'
      key={`page-button-${pNumber}`}
      aria-label={`Page Number ${pNumber}`}
      className={activePage === pNumber ? classes.activePageNumber : classes.pageNumber}
      onClick={() => handleNavigation(pNumber)}
    >
      {pNumber}
    </button>
  );

  return pageCount > 1 && (
    <div className={classes.paginationContainer}>
      <button
        type='button'
        key='previous-button'
        aria-label='Previous Page Button'
        disabled={activePage <= 1}
        className={activePage <= 1 ? classes.disabledNavigation : classes.enabledNavigation}
        onClick={() => activePage > 1 && handleNavigation(activePage - 1)}
      >
        <FontIcon className={classes.icon} icon='far fa-angle-left' />
        <span aria-label='Previous' className={classes.navigationLabel}>Previous</span>
      </button>
      <div className={classes.pagesWrapper}>
        <Default>
          {pageCount > 1 && getPageset(pageCount, 10, activePage).map(renderPageNumberButton)}
        </Default>
        <Mobile>
          {pageCount > 1 && getPageset(pageCount, 5, activePage).map(renderPageNumberButton)}
        </Mobile>
      </div>
      <button
        type='button'
        key='next-button'
        aria-label='Next Page Button'
        disabled={activePage === pageCount}
        className={activePage === pageCount ? classes.disabledNavigation : classes.enabledNavigation}
        onClick={() => activePage !== pageCount && handleNavigation(activePage + 1)}
      >
        <span aria-label='Next' className={classes.navigationLabel}>Next</span>
        <FontIcon className={classes.icon} icon='far fa-angle-right' />
      </button>
    </div>
  );
};

PaginationA.propTypes = {
  /**
   * Ref object to be used when scrolling after a new page is selected
  */
  containerRef: PropTypes.object,
  /**
   * Number of total pages 1...N
  */
  pageCount: PropTypes.number,
  /**
   * Callback to change the current active page
  */
  setActivePage: PropTypes.func.isRequired,
  /**
   * Number of current active page 1...N
  */
  activePage: PropTypes.number,
};

export default PaginationA;
